// ** React Imports
import { useState } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";
import Chip from "@mui/material/Chip";

// ** Logic Imports
import { useSettingPermission } from "../permissions/SettingPermissions";

// ** API
import { SaveTeams } from "screens/Settings/logic/teams"; 

const AddTeam = ({ 
  open, 
  handleClose,
  team,
  handleTeamChange,
  fetchTeamDetails,
  teamcontent,
  navigate,
  toast,
  employeesList,
  refreshTeams
}) => {
    //#region Permissions
    const { SettingPermissionProps } = useSettingPermission();
    const {
      hasTeamSavePermission,
    } = SettingPermissionProps;
    //#endregion Permissions

  const theme = useTheme();
  // console.log("team", team);
  const [errors, setErrors] = useState({
    teamName: false,
    teamMembers: false,
    crewLeaderID: false,
  });

  const handleSave = async () => {
    const newErrors = {
      teamName: !team?.teamName?.trim(),
      teamMembers: !Array.isArray(team?.teamMembers) || team.teamMembers.length === 0,
      crewLeaderID: !team?.crewLeaderID,
    };
  
    setErrors(newErrors);
  
    if (Object.values(newErrors).some(Boolean)) {
      toast.error("Please fill out all required fields", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      return;
    }
  
    try {
      const result = await SaveTeams(team, navigate);
      if (result && !result.isError) {
        toast.success(teamcontent.entity === "edit" ? "Team updated successfully" : "Team created successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        handleClose();
        fetchTeamDetails(0); 
        if (refreshTeams) refreshTeams();
      } else {
        toast.error(result?.message || "Failed to save team", {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
      }
    } catch (error) {
      console.error("Error saving team:", error);
      toast.error("An error occurred while saving the team", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    }
  };
  
  
  const crewLeaderOptions = employeesList.filter(e => e.roleID === 5);

  // 2️⃣ Figure out which option to show as selected.
  //    If `team.crewLeader` is a string (the name), match on name.
  //    If it’s already a number, match on employeeID.
  const selectedCrewLeader = crewLeaderOptions.find(opt => {
    if (typeof team.crewLeaderID === 'number') {
      return opt.employeeID === team.crewLeader;
    }
    // assume it's the full name string
    return `${opt.firstName} ${opt.lastName}` === team.crewLeader;
  }) || null;

  // console.log("team.crewLeader ID:", team);
  // console.log("selectedCrewLeader:", selectedCrewLeader);
return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle sx={{ borderBottom: "1px solid #D9D9D9", paddingBottom: "20px !important" }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
          {teamcontent.title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={5}>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fullWidth
              error={errors.teamName}
              helperText={errors.teamName ? "Team name is required" : ""}
              label={
                <>
                  Team Name <span style={{ color: 'red' }}>*</span>
                </>
              }
              placeholder="Enter your desired team name"
              value={team?.teamName || ""}
              onChange={(e) => {
                handleTeamChange("teamName", e.target.value);
                if (errors.teamName && e.target.value.trim()) {
                  setErrors(prev => ({ ...prev, teamName: false }));
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>          
              <CustomAutocomplete
                fullWidth
                options={(employeesList || [])
                  .slice()
                  .sort((a, b) => {
                    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                }
                getOptionLabel={(option) => option?.firstName + ' ' + option?.lastName || ""}
                value={employeesList?.find((item) => item.employeeID === team?.crewLeaderID)}
               onChange={(event, newValue) => {
                    handleTeamChange("crewLeaderID", newValue ? newValue.employeeID : 0);

                    if (errors.crewLeaderID && newValue) {
                      setErrors(prev => ({ ...prev, crewLeaderID: false }));
                    }
                  }}
                isOptionEqualToValue={(option, value) =>
                  option?.employeeID === value?.employeeID
                }
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    error={errors.crewLeaderID}
                    helperText={errors.crewLeaderID ? "Crew Leader is required" : ""}
                    label={
                      <>
                        Crew Leader <span style={{ color: 'red' }}>*</span>
                      </>
                    }
                    placeholder="Select Crew Leader"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <CustomAutocomplete
                multiple  
                fullWidth
                disableCloseOnSelect
                options={(employeesList.filter(x => x.employeeID !== team?.crewLeaderID) || [])
                  .slice()
                  .sort((a, b) => {
                    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                }
                getOptionLabel={(option) => option?.firstName + ' ' + option?.lastName || ""}
                getOptionDisabled={(option) => option.skillID === 0 || option.skillID === null}
                value={
                  (team?.teamMembers || []).map(id =>
                    employeesList.find(emp => emp.employeeID === id || emp.employeeID === id.employeeID)
                  ).filter(Boolean)
                }
               onChange={(event, newValue) => {
                const selectedIDs = newValue.map(item => item.employeeID);
                handleTeamChange("teamMembers", selectedIDs);

                if (errors.teamMembers && selectedIDs.length > 0) {
                  setErrors(prev => ({ ...prev, teamMembers: false }));
                }
              }}
                isOptionEqualToValue={(option, value) =>
                  option?.employeeID === value?.employeeID
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    <ListItemText primary={`${option.firstName} ${option.lastName}`} />
                  </li>
                )}
                renderTags={(value, getTagProps) => {
                  console.log("value", value);
                
                  const visibleCount = 3;
                  const extraCount = value.length - visibleCount;
                
                  return (
                    <>
                      {value.slice(0, visibleCount).map((item, index) => (
                        <Chip
                          key={index}
                          label={
                            <Typography
                              variant="body2"
                              sx={{ 
                                maxWidth: "70px", 
                                overflow: "hidden", 
                                textOverflow: "ellipsis", 
                                whiteSpace: "nowrap" 
                              }}
                            >
                              {item.firstName} {item.lastName}
                            </Typography>
                          }
                          {...getTagProps({ index })}
                        />
                      ))}
                
                      {extraCount > 0 && (
                        <Chip
                          label={`+${extraCount}`}
                          key="extra-count"
                          size="small"
                        />
                      )}
                    </>
                  );
                }}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    error={errors.teamMembers}
                    helperText={errors.teamMembers ? "Team Members are required" : ""}
                    label={
                      <>
                        Team Members <span style={{ color: 'red' }}>*</span>
                      </>
                    }
                    placeholder="Select Team Members"
                  />
                )}
              />
            </Grid>       
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
          zIndex: 1,
        }}
        className="dialog-actions-dense"
      >
        <Box>
          <Button variant="contained" color="info" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
         <Button 
  variant="contained" 
  color="main" 
  disabled={!hasTeamSavePermission}
  {...(hasTeamSavePermission && {
    onClick: () => handleSave()
  })}
>
  {teamcontent.entity === "edit" ? "Save Changes" : "Save Team"}
</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeam;
